import { FunctionComponent } from 'react'
import { Nav, Button } from 'react-bootstrap'
import useQueryParams from 'Frontend/hooks/useQueryParams'
import { useTranslation } from 'react-i18next'

interface PagingProps {
  noOfPages: number
  activePage: number
  callback: (_activePage: number) => void
}

const Paging: FunctionComponent<PagingProps> = ({ activePage, noOfPages, callback }) => {
  const { setQueryParam } = useQueryParams()
  const { t } = useTranslation()
  const isDesktop = true
  const noOfPagesToShow = isDesktop ? 4 : 0
  const pagesOnEachSide = noOfPagesToShow / 2
  const minPage =
    activePage - pagesOnEachSide > 1
      ? activePage - pagesOnEachSide
      : noOfPages - activePage < 2
        ? noOfPages - noOfPagesToShow < 1
          ? 1
          : noOfPages - noOfPagesToShow
        : 1

  const maxPage = minPage + noOfPagesToShow <= noOfPages ? minPage + noOfPagesToShow : noOfPages

  const onPageChanged = (newActivePage: number) => {
    setQueryParam('page', newActivePage.toString())
    callback(newActivePage)
  }

  return (
    <Nav className="justify-content-center mt-lg-5 mt-3">
      <ol className="list-unstyled d-flex gap-1 flex-wrap">
        {minPage > 1 && (
          <li className="paging__page-number">
            <Button
              variant="link"
              aria-current="false"
              className="paging__page-number-button"
              onClick={() => onPageChanged(1)}
            >
              <span className="visually-hidden">{t('previous-page')}</span>
              <svg className="rotate-180" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
                <path d="M0 3.795l2.995-2.98 11.132 11.185-11.132 11.186-2.995-2.981 8.167-8.205-8.167-8.205zm18.04 8.205l-8.167 8.205 2.995 2.98 11.132-11.185-11.132-11.186-2.995 2.98 8.167 8.206z" />
              </svg>
            </Button>
          </li>
        )}
        {activePage > 1 && (
          <li className="paging__page-number">
            <Button
              variant="link"
              className="paging__page-number-button"
              aria-current="false"
              onClick={() => onPageChanged(activePage - 1)}
            >
              <span className="visually-hidden">{t('first-page')}</span>
              <svg className="rotate-180" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
                <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
              </svg>
            </Button>
          </li>
        )}
        {minPage > 1 && (
          <li className="paging__page-number">
            <Button
              variant="link"
              aria-current="false"
              className="paging__page-number-button"
              onClick={() => onPageChanged(minPage - 1)}
            >
              ...
            </Button>
          </li>
        )}
        {(() => {
          const listItems = []
          for (let i = minPage; i <= maxPage; i += 1) {
            listItems.push(
              <li key={i} className={`paging__page-number ${i === activePage ? 'paging__active-page' : ''}`}>
                <Button
                  variant="link"
                  className="paging__page-number-button"
                  aria-current={activePage === i}
                  onClick={() => onPageChanged(i)}
                >
                  {i}
                </Button>
              </li>,
            )
          }
          return listItems
        })()}
        {maxPage < noOfPages && (
          <li className="paging__page-number">
            <Button
              variant="link"
              aria-current="false"
              className="paging__page-number-button"
              onClick={() => onPageChanged(maxPage + 1)}
            >
              ...
            </Button>
          </li>
        )}
        {activePage < noOfPages && (
          <li className="paging__page-number">
            <Button
              variant="link"
              className="paging__page-number-button"
              aria-current="false"
              onClick={() => onPageChanged(activePage + 1)}
            >
              <span className="visually-hidden">{t('next-page')}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
                <path d="M5 3l3.057-3 11.943 12-11.943 12-3.057-3 9-9z" />
              </svg>
            </Button>
          </li>
        )}
        {maxPage < noOfPages && (
          <li className="paging__page-number">
            <Button
              variant="link"
              className="paging__page-number-button"
              aria-current="false"
              onClick={() => onPageChanged(noOfPages)}
            >
              <span className="visually-hidden">{t('last-page')}</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
                <path d="M0 3.795l2.995-2.98 11.132 11.185-11.132 11.186-2.995-2.981 8.167-8.205-8.167-8.205zm18.04 8.205l-8.167 8.205 2.995 2.98 11.132-11.185-11.132-11.186-2.995 2.98 8.167 8.206z" />
              </svg>
            </Button>
          </li>
        )}
      </ol>
    </Nav>
  )
}

export default Paging
